import { apiErrorCode } from './apiErrorCode';
import { humanizeError } from './errors';

/**
 * Отказы ручки классификации курса (договор `docs/CONTRACTS/mirror-v2.md` §2).
 *
 * ⚠️ РАЗЛИЧАТЬ ПРИЧИНЫ ТОЛЬКО ПО `code`. Общий `humanizeError` на 409 отвечает
 * «такая запись уже существует» — для «курс вытолкнул бы своё в поток» это
 * просто неправда, а именно 409 здесь самый частый отказ.
 *
 * ⚠️ ТЕЛО ОТКАЗА ЛЕЖИТ В `raw`, а не в `response.data`: так его отдаёт клиент
 * API (`api/client.ts`, `normaliseError`). На этом уже обжигались 10.09.2026 —
 * см. шапку `apiErrorCode.ts`.
 */

const CODE_KEYS: Record<string, string> = {
  subtype_owner_only: 'marathonClassification.errOwnerOnly',
  confirm_title_mismatch: 'marathonClassification.errConfirmMismatch',
  invalid_subtype: 'marathonClassification.errInvalidSubtype',
  invalid_access_category: 'marathonClassification.errInvalidCategory',
  nothing_to_change: 'marathonClassification.errNothingToChange',
  mirror_busy: 'marathonClassification.errMirrorBusy',
  marathon_not_found: 'marathonClassification.errMarathonNotFound',
  invalid_body: 'marathonClassification.errInvalidBody',
  // `subtype_join_would_push` здесь НЕТ намеренно: у него не одна строка, а
  // числа по уровням — см. `joinConflict` ниже.
};

/** Уровни, по которым сторож входа считает расхождение. Порядок — сверху вниз по дереву курса. */
export const CONFLICT_LEVELS = ['module', 'lesson', 'section', 'block'] as const;
export type ConflictLevel = (typeof CONFLICT_LEVELS)[number];

export type SubtypeJoinConflict = {
  subtype: string;
  /** Чего нет в потоке: зеркало добавило бы это во все курсы подтипа. */
  wouldAdd: Partial<Record<ConflictLevel, number>>;
  /** Где курс свежее потока: его версия заменила бы версию живых курсов. */
  wouldOverwrite: Partial<Record<ConflictLevel, number>>;
};

const isObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

function errorBody(err: unknown): Record<string, unknown> | undefined {
  if (!isObject(err)) return undefined;
  if (isObject(err.raw)) return err.raw;
  const response = err.response;
  if (isObject(response) && isObject(response.data)) return response.data;
  return undefined;
}

/** Числа по уровням. Отсутствующий уровень — ноль, поэтому его просто нет в ответе. */
function levels(raw: unknown): Partial<Record<ConflictLevel, number>> {
  if (!isObject(raw)) return {};
  const out: Partial<Record<ConflictLevel, number>> = {};
  for (const level of CONFLICT_LEVELS) {
    const n = raw[level];
    if (typeof n === 'number' && Number.isFinite(n) && n > 0) out[level] = n;
  }
  return out;
}

/**
 * Разбор отказа «вход в подтип вытолкнул бы содержимое» (409).
 *
 * ⚠️ ЧИСЛА ЛЕЖАТ ВНУТРИ `data`, а не в корне тела. Без них отказ превращается в
 * «не получилось» — а человеку нужно знать, СКОЛЬКО уроков уехало бы в чужие
 * курсы, чтобы решить, копия это или уже своя работа.
 */
export function joinConflict(err: unknown): SubtypeJoinConflict | null {
  if (apiErrorCode(err) !== 'subtype_join_would_push') return null;
  const data = errorBody(err)?.data;
  if (!isObject(data)) return null;
  const subtype = typeof data.subtype === 'string' ? data.subtype : '';
  return {
    subtype,
    wouldAdd: levels(data.would_add),
    wouldOverwrite: levels(data.would_overwrite),
  };
}

export type OutsideMirrorLesson = { id: string; title: string };

export type SubtypeJoinOutsideMirror = {
  subtype: string;
  /** Сколько уроков курса зеркало не узнаёт (сам урок, его модуль или что-то внутри без метки). */
  lessonsTotal: number;
  /** Первые из них по порядку курса; сервер отдаёт не больше 50. */
  lessons: OutsideMirrorLesson[];
};

/**
 * Разбор отказа «в курсе уроки не из потока» (409 `subtype_join_outside_mirror`,
 * техдолг п. 268).
 *
 * ⚠️ ЭТО НЕ «ВЫТОЛКНУЛ БЫ». Курс ничего не разнесёт — наоборот, зеркало положило
 * бы весь поток рядом с его собственными уроками: 22.09.2026 так у курса
 * BEGINNER ONE TO ONE стало 46 уроков и два Ep-1. Поэтому свой код и свой текст.
 */
export function outsideMirror(err: unknown): SubtypeJoinOutsideMirror | null {
  if (apiErrorCode(err) !== 'subtype_join_outside_mirror') return null;
  const data = errorBody(err)?.data;
  if (!isObject(data)) return null;
  const lessons: OutsideMirrorLesson[] = Array.isArray(data.lessons)
    ? data.lessons.flatMap((l) =>
        isObject(l) && typeof l.id === 'string' && typeof l.title === 'string'
          ? [{ id: l.id, title: l.title }]
          : [],
      )
    : [];
  const total = data.lessons_total;
  return {
    subtype: typeof data.subtype === 'string' ? data.subtype : '',
    lessonsTotal:
      typeof total === 'number' && Number.isFinite(total) && total > 0 ? total : lessons.length,
    lessons,
  };
}

/**
 * Текст отказа: свой по коду, иначе общий.
 *
 * 409 со сторожем входа сюда НЕ попадает — его показывает карточка отдельно, с
 * числами; здесь для него остаётся общая строка на случай ответа без `data`.
 * То же для 409 «уроки не из потока».
 */
export function classificationErrorText(
  err: unknown,
  t: (key: string, vars?: Record<string, unknown>) => string,
): string {
  const code = apiErrorCode(err);
  const key = code ? CODE_KEYS[code] : undefined;
  if (key) return t(key);
  if (code === 'subtype_join_would_push') return t('marathonClassification.errWouldPushShort');
  if (code === 'subtype_join_outside_mirror') return t('marathonClassification.errOutsideMirrorShort');
  return humanizeError(err);
}
